import { useEffect, useState } from 'react';

import { Dashboard } from 'types/dashboard';
import { DashboardWidget } from 'types/dashboard_widget';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useDashboards } from 'dashboards/hooks/useDashboards';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';
import { addNotification, addErrorNotification } from 'services/notification';
import { useAPI } from 'hooks/api/useAPI';

import { Icon } from 'components/Icon/IconDeprecated';

type AddToDashboardMenuProps = {
  createWidget: () => DashboardWidget;
};

export function AddToDashboardMenu({ createWidget }: AddToDashboardMenuProps) {
  const { applicationID, client } = useAPI();
  const [isAdding, setIsAdding] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const { data: dashboardsData, isLoading: isLoadingDashboards } =
    useDashboards({ limit: 100 });

  const { mutateAsync: updateDashboard } = useUpdateDashboard();

  const dashboards = dashboardsData?.data || [];

  // Reset success state after showing checkmark
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const handleAddToDashboard = async (
    dashboardId: string,
    dashboardName: string
  ) => {
    setIsAdding(true);
    setIsOpen(false);

    try {
      // Fetch the full dashboard data
      const { data: dashboard } = await client.get<Dashboard>(
        `/internal/apps/${applicationID}/dashboards/${dashboardId}`
      );

      const widget = createWidget();
      const newId = crypto.randomUUID();
      widget.id = newId;

      const newLayout = getNewWidgetLayout(
        dashboard.configuration.widgets,
        widget.layout.w,
        widget.layout.h
      );
      widget.layout = { ...newLayout, i: newId };

      const updatedDashboard = upsertWidget(dashboard, widget);

      await updateDashboard({
        id: dashboard.id,
        configuration: updatedDashboard.configuration,
      });

      setShowSuccess(true);
      addNotification({
        title: 'Widget added',
        message: `Added to "${dashboardName}"`,
        variant: 'success',
      });
    } catch {
      addErrorNotification({
        title: 'Error',
        message: 'Failed to add widget to dashboard',
      });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        title="Add to dashboard"
        onClick={() => !showSuccess && setIsOpen(!isOpen)}
        disabled={isAdding}
        className="flex h-6 w-6 items-center justify-center rounded text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 disabled:opacity-50 dark:hover:bg-neutral-700 dark:hover:text-neutral-300"
        aria-label="Add to dashboard"
      >
        {showSuccess ? (
          <Icon name="check-circle" size="medium" className="text-green-600" />
        ) : (
          <Icon name="plus" size="small"/>
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close menu */}
          <div
            className="fixed inset-0 z-[10000]"
            onClick={() => setIsOpen(false)}
          />
          {/* Dropdown menu */}
          <div className="absolute bottom-full right-0 z-[10001] mb-1 w-[200px] rounded-md border border-neutral-200 bg-white shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
            <div className="px-2 py-1 text-xs font-medium text-neutral-500">
              Add to dashboard
            </div>
            <div className="max-h-[140px] overflow-y-auto">
              {isLoadingDashboards && (
                <div className="px-2 py-1.5 text-sm text-neutral-400">
                  Loading...
                </div>
              )}
              {!isLoadingDashboards && dashboards.length === 0 && (
                <div className="px-2 py-1.5 text-sm text-neutral-400">
                  No dashboards found
                </div>
              )}
              {dashboards.map((dashboard) => (
                <button
                  key={dashboard.id}
                  type="button"
                  onClick={() =>
                    handleAddToDashboard(dashboard.id, dashboard.name)
                  }
                  disabled={isAdding}
                  className="block w-full truncate px-2 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-100 disabled:opacity-50 dark:text-neutral-200 dark:hover:bg-neutral-700"
                >
                  {dashboard.name}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
